import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, UNSAFE_NavigationContext } from 'react-router';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import { getButtonColor, getButtonHoverColor } from '../../theme/themeUtils';
import { useOrderCreation } from '../../context/OrderCreationContext';
import { Z_INDEX } from '../../constants/zIndex';
import { useContext } from 'react';
import { categoryKeyToPath } from '../../util/pathUtils';
import { CustomButton } from './CustomButton';
import { AppDialog } from './AppDialog';
import { responsiveFontSize, stackedActionButtonMinWidthSx } from '../../util/responsiveUtils';

// Helper to extract pathname from a URL (removes query string)
const getPathname = (url: string): string => {
    try {
        // Handle relative URLs
        if (url.startsWith('/')) {
            return url.split('?')[0].split('#')[0];
        }
        const parsed = new URL(url, window.location.origin);
        return parsed.pathname;
    } catch {
        return url.split('?')[0].split('#')[0];
    }
};

// Check if navigation is within the same order form (same pathname, different step)
const isSamePathNavigation = (from: string, to: string): boolean => {
    const fromPath = getPathname(from);
    const toPath = getPathname(to);
    return fromPath === toPath;
};

// Module-level refs and listener so we run BEFORE React Router's popstate listener (which is
// registered when Router mounts). This fixes browser back button not showing the blocker.
const sharedProtectedPathRef: { current: string | null } = { current: null };
const sharedOrderFormUrlRef: { current: string } = { current: '' };
const ignoreNextPopStateRef: { current: boolean } = { current: false };
const SHOW_LEAVE_DIALOG_EVENT = 'virtoly-order-leave-dialog';

function handlePopStateEarly() {
    if (ignoreNextPopStateRef.current) {
        ignoreNextPopStateRef.current = false;
        return;
    }
    const protectedPath = sharedProtectedPathRef.current;
    if (!protectedPath) return;
    const newPathname = window.location.pathname;
    if (isSamePathNavigation(protectedPath, newPathname)) return;
    const urlToRestore = sharedOrderFormUrlRef.current;
    if (urlToRestore) {
        // Restore URL in place with replaceState so we don't navigate back then forward
        // (avoids visible "go back then come back" flash)
        window.history.replaceState(null, '', urlToRestore);
    }
    window.dispatchEvent(new CustomEvent(SHOW_LEAVE_DIALOG_EVENT, { detail: { reason: 'back' } }));
}

if (typeof window !== 'undefined') {
    window.addEventListener('popstate', handlePopStateEarly);
}

export function NavigationBlocker() {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { isCreatingOrder, clearOrderCreation, orderCategory } = useOrderCreation();
    const navigationContext = useContext(UNSAFE_NavigationContext);
    
    const [showDialog, setShowDialog] = useState(false);
    const [pendingLocation, setPendingLocation] = useState<string | null>(null);
    const [pendingState, setPendingState] = useState<any>(null);
    
    // Track the PROTECTED pathname (only the pathname where order creation started)
    // This doesn't change when navigating between steps
    const protectedPathnameRef = useRef<string | null>(null);
    const shouldBlockRef = useRef(false);
    
    // Track the current URL within the form (for restoration on blocked navigation)
    const currentUrlRef = useRef(location.pathname + location.search);
    
    // Flag to skip the next popstate event (used when we call history.forward() to restore)
    const isRestoringNavigationRef = useRef(false);

    // Keep track of current blocking state and set up protection when blocking starts
    useEffect(() => {
        shouldBlockRef.current = isCreatingOrder;
        
        if (isCreatingOrder && !protectedPathnameRef.current) {
            // Store the pathname we're protecting (the order form)
            const url = location.pathname + location.search;
            protectedPathnameRef.current = location.pathname;
            sharedProtectedPathRef.current = location.pathname;
            sharedOrderFormUrlRef.current = url;
            currentUrlRef.current = url;
        } else if (!isCreatingOrder && protectedPathnameRef.current && location.pathname !== protectedPathnameRef.current) {
            // Defer clearing so our popstate handler (which runs after React Router's) still sees
            // the ref when user presses browser back. Only clear next tick and only if we're still
            // off the protected path (we might have blocked and called history.forward()).
            const pathToClear = protectedPathnameRef.current;
            const timer = setTimeout(() => {
                if (window.location.pathname !== pathToClear) {
                    protectedPathnameRef.current = null;
                    sharedProtectedPathRef.current = null;
                    sharedOrderFormUrlRef.current = '';
                }
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isCreatingOrder, location.pathname, location.search]);
    
    // Update currentUrlRef and sharedOrderFormUrlRef when navigating within the protected form
    useEffect(() => {
        if (isCreatingOrder && protectedPathnameRef.current) {
            if (location.pathname === protectedPathnameRef.current) {
                const url = location.pathname + location.search;
                currentUrlRef.current = url;
                sharedOrderFormUrlRef.current = url;
            }
        }
    }, [isCreatingOrder, location.pathname, location.search]);

    // Intercept navigation using the navigator's push/replace
    useEffect(() => {
        if (!navigationContext?.navigator) return;

        const navigator = navigationContext.navigator;
        const originalPush = navigator.push.bind(navigator);
        const originalReplace = navigator.replace.bind(navigator);

        const createInterceptor = (originalFn: Function) => (to: any, state?: any, ...rest: any[]) => {
            // Check current blocking state
            if (!shouldBlockRef.current) {
                return originalFn(to, state, ...rest);
            }

            let targetPath: string;
            let navigationState: any = null;
            
            // React Router v6 navigator can receive:
            // 1. navigator.push(pathname, state) - two separate arguments
            // 2. navigator.push({ pathname, state }) - single object
            // 3. navigator.push(location) - Location object
            
            if (typeof to === 'string') {
                targetPath = to;
                navigationState = state || null;
            } else if (to && typeof to === 'object') {
                // Could be a Location object or navigation options object
                if ('pathname' in to) {
                    targetPath = to.pathname;
                    // Include search params if present
                    if ('search' in to && to.search) {
                        targetPath += to.search;
                    }
                    navigationState = to.state || state || null;
                } else {
                    // Might be a different structure, try to extract pathname
                    targetPath = to.path || to.pathname || String(to);
                    navigationState = to.state || state || null;
                }
            } else {
                return originalFn(to, state, ...rest);
            }

            // Allow navigation within the same pathname (step changes via query params)
            // This allows step navigation to work without blocking
            const protectedPath = protectedPathnameRef.current || location.pathname;
            if (isSamePathNavigation(protectedPath, targetPath)) {
                return originalFn(to, state, ...rest);
            }

            // Block the navigation and show dialog
            setPendingLocation(targetPath);
            setPendingState(navigationState);
            setShowDialog(true);
        };

        navigator.push = createInterceptor(originalPush);
        navigator.replace = createInterceptor(originalReplace);

        return () => {
            navigator.push = originalPush;
            navigator.replace = originalReplace;
        };
    }, [navigationContext]);

    // Intercept <a> tag clicks (for footer and other direct links)
    useEffect(() => {
        const handleLinkClick = (event: MouseEvent) => {
            if (!shouldBlockRef.current) return;

            const target = event.target as HTMLElement;
            const anchor = target.closest('a');
            
            if (!anchor) return;
            
            const href = anchor.getAttribute('href');
            
            // Skip external links, anchors, and non-navigation links
            if (!href || 
                href.startsWith('http') || 
                href.startsWith('mailto:') || 
                href.startsWith('tel:') ||
                href.startsWith('#') ||
                anchor.getAttribute('target') === '_blank') {
                return;
            }

            // Allow navigation within the same pathname (step changes)
            const protectedPath = protectedPathnameRef.current || window.location.pathname;
            if (isSamePathNavigation(protectedPath, href)) {
                return;
            }

            // Prevent the default navigation
            event.preventDefault();
            event.stopPropagation();
            
            // Show our custom dialog
            setPendingLocation(href);
            setShowDialog(true);
        };

        // Use capture phase to catch events before they're handled
        document.addEventListener('click', handleLinkClick, true);

        return () => {
            document.removeEventListener('click', handleLinkClick, true);
        };
    }, []);

    // Listen for the custom event dispatched by the early popstate handler (runs before React Router)
    useEffect(() => {
        const onShowLeaveDialog = () => {
            const urlToRestore = sharedOrderFormUrlRef.current;
            if (urlToRestore) {
                navigate(urlToRestore, { replace: true });
            }
            setPendingLocation('__BACK__');
            setShowDialog(true);
        };
        window.addEventListener(SHOW_LEAVE_DIALOG_EVENT, onShowLeaveDialog);
        return () => window.removeEventListener(SHOW_LEAVE_DIALOG_EVENT, onShowLeaveDialog);
    }, [navigate]);

    // Fallback popstate listener (in case early listener didn't run, e.g. hot reload).
    useEffect(() => {
        const handlePopState = (_event: PopStateEvent) => {
            if (isRestoringNavigationRef.current || ignoreNextPopStateRef.current) {
                isRestoringNavigationRef.current = false;
                ignoreNextPopStateRef.current = false;
                return;
            }
            const protectedPath = protectedPathnameRef.current;
            if (!protectedPath) return;
            const newPathname = window.location.pathname;
            const newUrl = window.location.pathname + window.location.search;
            if (isSamePathNavigation(protectedPath, newPathname)) {
                currentUrlRef.current = newUrl;
                return;
            }
            isRestoringNavigationRef.current = true;
            const urlToRestore = currentUrlRef.current;
            if (urlToRestore) {
                window.history.replaceState(null, '', urlToRestore);
            }
            navigate(urlToRestore || window.location.pathname + window.location.search, { replace: true });
            setPendingLocation('__BACK__');
            setShowDialog(true);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [navigate]);

    // Handle browser close/refresh - use native dialog for this
    useEffect(() => {
        if (!isCreatingOrder) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = 'You have an order in progress. Are you sure you want to leave?';
            return event.returnValue;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isCreatingOrder]);

    const handleStay = useCallback(() => {
        setShowDialog(false);
        setPendingLocation(null);
        setPendingState(null);
    }, []);

    const handleLeave = useCallback(() => {
        const targetLocation = pendingLocation;
        const targetState = pendingState;
        
        // Clear the blocking state FIRST (synchronously via ref)
        shouldBlockRef.current = false;
        protectedPathnameRef.current = null;
        sharedProtectedPathRef.current = null;
        sharedOrderFormUrlRef.current = '';

        // Clear the order creation state
        clearOrderCreation();
        
        // Reset dialog state
        setShowDialog(false);
        setPendingLocation(null);
        setPendingState(null);
        
        // Navigate based on how the navigation was triggered
        if (targetLocation === '__BACK__') {
            // For back button navigation, navigate to a known destination
            // based on the order category. This avoids issues with unpredictable
            // history stack from step navigation within the order form.
            if (orderCategory) {
                // Convert category name to URL slug using categoryKeyToPath to handle "&" properly
                // (e.g., "Tech & Electronics" -> "tech-and-electronics")
                const categoryPath = categoryKeyToPath(orderCategory);
                navigate(`/customer-service/${categoryPath}`, { replace: true });
            } else {
                // Fallback to main service selection page
                navigate('/customer-create-order', { replace: true });
            }
        } else if (targetLocation) {
            // If navigating to customer-create-order with preselected category and subcategory,
            // navigate directly to the service page with the subcategory
            if (targetLocation === '/customer-create-order' && 
                targetState?.preselectedCategory && 
                targetState?.preselectedSubcategory) {
                const categoryPath = categoryKeyToPath(targetState.preselectedCategory);
                const subcategory = encodeURIComponent(targetState.preselectedSubcategory);
                navigate(`/customer-service/${categoryPath}?subcategory=${subcategory}`, { replace: true });
            } else {
                // Navigate with preserved state if available
                navigate(targetLocation, { state: targetState || undefined, replace: false });
            }
        }
    }, [pendingLocation, pendingState, clearOrderCreation, navigate, orderCategory]);

    const titleNode = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
            <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: responsiveFontSize.headingMd }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: responsiveFontSize.subtitle, color: theme.palette.text.primary }}>
                Cancel Order?
            </Typography>
        </Box>
    );

    return (
        <AppDialog
            open={showDialog}
            onClose={handleStay}
            title={titleNode}
            maxWidth="xs"
            fullWidth
            zIndex={Z_INDEX.FORCE_UPDATE_MODAL}
            actionsSx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 1.5 },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
                justifyContent: { xs: 'center', sm: 'flex-end' },
                '& > *': { margin: '0 !important' },
                '& .MuiButton-root': { minWidth: stackedActionButtonMinWidthSx },
            }}
            actions={
                <>
                    <CustomButton
                        onClick={handleStay}
                        variant="contained"
                        size="medium"
                        sx={{
                            backgroundColor: getButtonColor(theme),
                            '@media (hover: hover)': { '&:hover': { backgroundColor: getButtonHoverColor(theme) } },
                            textTransform: 'none',
                            borderRadius: 2,
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: responsiveFontSize.bodyMenu,
                            order: { xs: 1, sm: 0 },
                            minHeight: { xs: 44, sm: 40 },
                            minWidth: stackedActionButtonMinWidthSx,
                        }}
                    >
                        Stay & Continue
                    </CustomButton>
                    <CustomButton
                        onClick={handleLeave}
                        variant="outlined"
                        size="medium"
                        color={theme.palette.error.main}
                        textColor={theme.palette.error.main}
                        sx={{
                            borderColor: theme.palette.error.main,
                            '@media (hover: hover)': {
                                '&:hover': {
                                    borderColor: theme.palette.error.dark,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.08)' : 'rgba(211, 47, 47, 0.04)',
                                },
                            },
                            textTransform: 'none',
                            borderRadius: 2,
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: responsiveFontSize.bodyMenu,
                            order: { xs: 2, sm: 1 },
                            minHeight: { xs: 44, sm: 40 },
                            minWidth: stackedActionButtonMinWidthSx,
                        }}
                    >
                        Leave & Cancel Order
                    </CustomButton>
                </>
            }
        >
            <Typography sx={{ color: theme.palette.text.primary, fontSize: responsiveFontSize.body }}>
                Are you sure you want to leave? Your order progress will be lost.
            </Typography>
        </AppDialog>
    );
}
